import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { settingsAPI, allowedIPsAPI } from '../../services/adminApi';
import ConfirmDialog from '../common/ConfirmDialog';

export default function IPManagement() {
  const [ips, setIps] = useState([]);
  const [ipRestrictionEnabled, setIpRestrictionEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [form, setForm] = useState({ ip_address: '', label: '' });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ipsRes, settingsRes] = await Promise.all([
        allowedIPsAPI.list(),
        settingsAPI.get(),
      ]);
      setIps(ipsRes.data.results || ipsRes.data);
      const s = Array.isArray(settingsRes.data) ? settingsRes.data[0] : settingsRes.data;
      setIpRestrictionEnabled(s.ip_restriction_enabled);
    } catch (err) {
      setError('Errore nel caricamento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggleRestriction = async () => {
    try {
      const newValue = !ipRestrictionEnabled;
      await settingsAPI.update({ ip_restriction_enabled: newValue });
      setIpRestrictionEnabled(newValue);
      setSuccess(newValue ? 'Restrizione IP attivata.' : 'Restrizione IP disattivata.');
    } catch (err) {
      setError('Errore nel salvataggio.');
    }
  };

  const handleAddIP = async () => {
    try {
      setSaving(true);
      setError('');
      await allowedIPsAPI.create(form);
      setDialogOpen(false);
      setForm({ ip_address: '', label: '' });
      setSuccess('IP aggiunto.');
      loadData();
    } catch (err) {
      const detail = err.response?.data;
      if (detail?.ip_address) {
        setError(detail.ip_address.join(' '));
      } else {
        setError('Errore nell\'aggiunta dell\'IP.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleIP = async (ip) => {
    try {
      const res = await allowedIPsAPI.toggle(ip.id);
      setIps((prev) => prev.map((item) => (item.id === ip.id ? res.data : item)));
    } catch (err) {
      setError('Errore nel toggle.');
    }
  };

  const handleDeleteIP = async () => {
    if (!deleteConfirm) return;
    try {
      await allowedIPsAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      setSuccess('IP eliminato.');
      loadData();
    } catch (err) {
      setError('Errore nell\'eliminazione.');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Restrizione per IP
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Se attiva, solo gli IP elencati possono accedere al file manager.
          </Typography>
        </Box>
        <FormControlLabel
          control={<Switch checked={ipRestrictionEnabled} onChange={handleToggleRestriction} />}
          label={ipRestrictionEnabled ? 'Attiva' : 'Disattiva'}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          IP autorizzati ({ips.length})
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)} size="small">
          Aggiungi IP
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Indirizzo IP</TableCell>
              <TableCell>Etichetta</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Creato da</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ips.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nessun IP configurato.
                </TableCell>
              </TableRow>
            )}
            {ips.map((ip) => (
              <TableRow key={ip.id}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{ip.ip_address}</TableCell>
                <TableCell>{ip.label || '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={ip.is_active ? 'Attivo' : 'Disattivo'}
                    color={ip.is_active ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>{ip.created_by_username || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title={ip.is_active ? 'Disattiva' : 'Attiva'}>
                    <IconButton size="small" onClick={() => handleToggleIP(ip)}>
                      {ip.is_active ? <ToggleOn color="success" /> : <ToggleOff />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina">
                    <IconButton size="small" color="error" onClick={() => setDeleteConfirm(ip)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add IP Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aggiungi IP autorizzato</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Indirizzo IP"
            value={form.ip_address}
            onChange={(e) => setForm((f) => ({ ...f, ip_address: e.target.value }))}
            placeholder="192.168.1.100"
            size="small"
            autoFocus
          />
          <TextField
            label="Etichetta (opzionale)"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Ufficio principale"
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            onClick={handleAddIP}
            disabled={!form.ip_address || saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            Aggiungi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={Boolean(deleteConfirm)}
        title="Elimina IP"
        message={`Eliminare l'IP ${deleteConfirm?.ip_address}?`}
        onConfirm={handleDeleteIP}
        onCancel={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}
